import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { utils } from '../utils';
import { theme } from '../constants';
import { components } from '../components';
import { AttachmentViewer } from '../components/AttachmentViewer';
import axiosInstance from '../services/axiosInstance';
import { RootState } from '../store';
import { useLanguage } from '../context/LanguageContext';
import { MdCheckCircle, MdCancel, MdPendingActions, MdDateRange, MdSubject, MdPerson, MdError, MdWarning } from 'react-icons/md';

// Fix for TS2786: 'Icon' cannot be used as a JSX component
const CheckCircleIcon = MdCheckCircle as any;
const CancelIcon = MdCancel as any;
const PendingActionsIcon = MdPendingActions as any;
const DateRangeIcon = MdDateRange as any;
const SubjectIcon = MdSubject as any;
const PersonIcon = MdPerson as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;

export const LeaveDetails: React.FC = () => {
  const { state, search } = useLocation();
  const navigate = hooks.useAppNavigate();
  const authSlice = hooks.useAppSelector((state: RootState) => state.authSlice);
  const { t, language } = useLanguage();
  const userPermissions = (authSlice as any).permissions || [];
  const canManageLeave = userPermissions.includes('leave:approve') || userPermissions.includes('leave:reject');
  
  const searchParams = new URLSearchParams(search);
  const leaveIdFromUrl = searchParams.get('id');

  const [leave, setLeave] = useState<any>(state?.leave);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(!state?.leave && !!leaveIdFromUrl);

  useEffect(() => {
    if (!leave && leaveIdFromUrl) {
      const fetchLeave = async () => {
        try {
          const response = await axiosInstance.get('/leaves');
          const foundLeave = response.data.find((l: any) => l.id.toString() === leaveIdFromUrl);
          if (foundLeave) {
            setLeave(foundLeave);
          }
        } catch (error) {
          console.error(`Failed to fetch leave:`, error);
        } finally {
          setIsFetching(false);
        }
      };
      fetchLeave();
    }
  }, [leave, leaveIdFromUrl]);

  const [showConfirmModal, setShowConfirmModal] = useState<'approved' | 'rejected' | 'delete' | null>(null);
  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning';
    onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';

  if (isFetching) {
    return (
      <div id='screen' style={{ ...utils.flexCenter(), height: '100vh', flexDirection: 'column' }}>
        <components.Header title={t.leaveDetails.title} goBack={true} />
        <text.T16>{t.common.loading}</text.T16>
      </div>
    );
  }

  if (!leave) {
    return (
      <div id='screen' style={{ ...utils.flexCenter(), height: '100vh', flexDirection: 'column' }}>
        <text.T16>{t.leaveDetails.notFound}</text.T16>
        <components.Button title={t.common.goBack} onClick={() => navigate(-1)} containerStyle={{ marginTop: 20, width: 200 }} />
      </div>
    );
  }

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void) => {
    setAlertConfig({ isOpen: true, title, message, type, onClose });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
    if (onClose) onClose();
  };

  const confirmAction = async () => {
    if (!showConfirmModal) return;
    const action = showConfirmModal;
    setShowConfirmModal(null);

    try {
      setIsLoading(true);
      if (action === 'delete') {
        await axiosInstance.delete(`/leaves/${leave.id}`);
        showAlert(t.common.success, t.leaveDetails.successWithdraw, 'success', () => navigate(-1));
      } else {
        const res = await axiosInstance.put(`/leaves/${leave.id}/status`, { status: action });
        setLeave((prev: any) => ({ ...prev, ...res.data.data }));
        showAlert(t.common.success, action === 'approved' ? t.leaveDetails.successApproved : t.leaveDetails.successRejected, 'success');
      }
    } catch (error) {
      console.error(`Failed to ${action} leave:`, error);
      showAlert(t.common.error, t.leaveDetails.errorAction, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved': return { color: '#10B981', bg: '#D1FAE5', icon: <CheckCircleIcon size={30} />, label: t.leaveDetails.status.approved };
      case 'rejected': return { color: '#EF4444', bg: '#FEE2E2', icon: <CancelIcon size={30} />, label: t.leaveDetails.status.rejected };
      case 'cancelled': return { color: '#6B7280', bg: '#F3F4F6', icon: <CancelIcon size={30} />, label: (t.leaveDetails.status as any).cancelled || 'Cancelled' };
      case 'pending':
      default: return { color: '#F59E0B', bg: '#FEF3C7', icon: <PendingActionsIcon size={30} />, label: t.leaveDetails.status.pending };
    }
  };

  const getLeaveTypeTranslation = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'paid time off (pto)': return 'Paid Time Off (PTO)';
      case 'sick leave': return t.leaveRequest.sickLeave;
      case 'unpaid leave': return t.leaveRequest.unpaidLeave;
      case 'maternity leave': return t.leaveRequest.maternityLeave;
      case 'marriage leave': return t.leaveRequest.marriageLeave;
      default: return type;
    }
  };

  const renderConfirmModal = () => {
    if (!showConfirmModal) return null;
    
    let title = '';
    let message = '';
    let confirmText = '';
    let confirmColor = '';
    
    if (showConfirmModal === 'delete') {
      title = t.leaveDetails.modal.cancelTitle;
      message = t.leaveDetails.modal.cancelMsg;
      confirmText = t.leaveDetails.modal.cancelBtn;
      confirmColor = '#EF4444';
    } else if (showConfirmModal === 'approved') {
      title = t.leaveDetails.modal.approveTitle;
      message = t.leaveDetails.modal.approveMsg;
      confirmText = t.leaveDetails.modal.approveBtn;
      confirmColor = '#10B981';
    } else if (showConfirmModal === 'rejected') {
      title = t.leaveDetails.modal.rejectTitle;
      message = t.leaveDetails.modal.rejectMsg;
      confirmText = t.leaveDetails.modal.rejectBtn;
      confirmColor = '#EF4444';
    }

    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 5000,
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 320, textAlign: 'center',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        }}>
          <text.H4 style={{marginBottom: 12}}>{title}</text.H4>
          <text.T16 style={{marginBottom: 24, color: theme.colors.bodyTextColor}}>
            {message}
          </text.T16>
          <div style={{display: 'flex', gap: 12}}>
            <components.Button 
              title={t.common.goBack} 
              style={{backgroundColor: '#F3F4F6', color: '#4B5563', padding: 10 }} 
              onClick={() => setShowConfirmModal(null)} 
            />
            <components.Button 
              title={confirmText} 
              style={{backgroundColor: confirmColor, padding: 10 }}
              onClick={confirmAction} 
            />
          </div>
        </div>
      </div>
    );
  };

  const renderAlert = () => {
    if (!alertConfig.isOpen) return null;
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 6000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, width: '100%', maxWidth: 320, textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{marginBottom: 16}}>
            {alertConfig.type === 'success' && <CheckCircleIcon size={60} color="#10B981" />}
            {alertConfig.type === 'error' && <ErrorIcon size={60} color="#EF4444" />}
            {alertConfig.type === 'warning' && <WarningIcon size={60} color="#F59E0B" />}
          </div>
          <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
          <text.T16 style={{ marginBottom: 24, color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>{alertConfig.message}</text.T16>
          <components.Button title={t.common.ok} onClick={hideAlert} containerStyle={{width: '100%'}} />
        </div>
      </div>
    );
  };

  const status = getStatusConfig(leave.status);
  const locale = language === 'id' ? 'id-ID' : 'en-US';
  const startDate = new Date(leave.start_date).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  const endDate = new Date(leave.end_date).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  const createdAt = new Date(leave.created_at).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div id='screen' style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <components.Header title={t.leaveDetails.title} goBack={true} />
      
      <main className='container' style={{ marginTop: 60, paddingBottom: 100 }}>
        {/* Status Card */}
        <div style={{
          backgroundColor: status.bg, padding: 24, borderRadius: 16, marginBottom: 24,
          display: 'flex', alignItems: 'center', gap: 16, border: `1px solid ${status.color}30`
        }}>
          <div style={{ color: status.color }}>{status.icon}</div>
          <div>
            <text.T14 style={{ color: status.color, opacity: 0.8, marginBottom: 4 }}>{t.leaveDetails.currentStatus}</text.T14>
            <text.H4 style={{ color: status.color }}>{status.label}</text.H4>
          </div>
        </div>

        {/* Detail Info */}
        <div style={{ backgroundColor: 'white', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20 }}>
          <text.H5 style={{ marginBottom: 16, borderBottom: '1px solid #F3F4F6', paddingBottom: 10 }}>{t.leaveDetails.leaveInfo}</text.H5>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ color: '#9CA3AF', marginTop: 2 }}><SubjectIcon size={20} /></div>
              <div>
                <text.T12 style={{ color: '#6B7280', marginBottom: 4 }}>{t.leaveDetails.leaveType}</text.T12>
                <text.T16 style={{ fontWeight: '500', textTransform: 'capitalize' }}>{getLeaveTypeTranslation(leave.leave_type)}</text.T16>
              </div>
            </div>

            {leave.user_name && (
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ color: '#9CA3AF', marginTop: 2 }}><PersonIcon size={20} /></div>
                <div>
                  <text.T12 style={{ color: '#6B7280', marginBottom: 4 }}>{t.leaveDetails.submittedBy}</text.T12>
                  <text.T16 style={{ fontWeight: '500', textTransform: 'capitalize' }}>{leave.user_name} ({leave.user_group || 'Unknown'})</text.T16>
                </div>
              </div>
            )}

            
            {leave.leave_type === 'Paid Time Off (PTO)' && (
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ color: '#9CA3AF', marginTop: 2 }}><DateRangeIcon size={20} /></div>
                <div>
                  <text.T12 style={{ color: '#6B7280', marginBottom: 4 }}>{t.leaveDetails.leaveBalance}</text.T12>
                  <text.T16 style={{ fontWeight: '500' }}>
                    {leave.leave_balance ?? leave.leave_total ?? 12} {t.leaveDetails.days}
                  </text.T16>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ color: '#9CA3AF', marginTop: 2 }}><DateRangeIcon size={20} /></div>
              <div>
                <text.T12 style={{ color: '#6B7280', marginBottom: 4 }}>{t.leaveDetails.dates}</text.T12>
                <text.T16 style={{ fontWeight: '500' }}>
                  {leave.start_date === leave.end_date ? startDate : `${startDate} - ${endDate}`}
                </text.T16>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ color: '#9CA3AF', marginTop: 2 }}><DateRangeIcon size={20} /></div>
              <div>
                <text.T12 style={{ color: '#6B7280', marginBottom: 4 }}>{t.leaveDetails.duration}</text.T12>
                <text.T16 style={{ fontWeight: '500' }}>{leave.duration_days} {t.leaveDetails.days}</text.T16>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ color: '#9CA3AF', marginTop: 2 }}><SubjectIcon size={20} /></div>
              <div>
                <text.T12 style={{ color: '#6B7280', marginBottom: 4 }}>{t.leaveDetails.reason}</text.T12>
                <text.T16 style={{ lineHeight: 1.5, color: theme.colors.mainDark }}>{leave.reason}</text.T16>
              </div>
            </div>

            {leave.attachment_url && (() => {
              const fileName = (leave.attachment_url as string).split(/[/\\]/).pop() || 'Lampiran';
              const fileUrl = `${imageBaseUrl}${(leave.attachment_url as string).replace(/\\/g, '/')}`;
              return (
                <div style={{ marginTop: 8 }}>
                  <text.T12 style={{ color: '#6B7280', marginBottom: 8 }}>{t.leaveDetails.attachmentAvailable}</text.T12>
                  <AttachmentViewer items={[{ url: fileUrl, name: fileName }]} />
                </div>
              );
            })()}

          </div>

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #F3F4F6', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <text.T12 style={{ color: '#9CA3AF' }}>{t.leaveDetails.createdAt}: {createdAt}</text.T12>
            {leave.approved_by_name && (leave.status === 'approved' || leave.status === 'rejected') && (
              <text.T12 style={{ color: '#9CA3AF' }}>
                {leave.status === 'approved'
                  ? (t.leaveDetails as any).approvedBy || 'Approved by'
                  : (t.leaveDetails as any).rejectedBy || 'Rejected by'}
                {': '}
                {leave.approved_by_name}
                {leave.approved_at && ` · ${new Date(leave.approved_at).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
              </text.T12>
            )}
          </div>
        </div>
      </main>

      {/* Action Buttons */}
      {leave.status === 'pending' && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: 'white',
          padding: '16px 24px 32px', boxShadow: '0 -4px 20px rgba(0,0,0,0.05)', zIndex: 10
        }}>
          <div className='container' style={{ padding: 0, maxWidth: 650, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {canManageLeave && (
              <div style={{ display: 'flex', gap: 12, width: '100%' }}>
                <components.Button title={isLoading ? t.leaveDetails.processing : t.leaveDetails.modal.rejectBtn} style={{ backgroundColor: '#FEE2E2', color: '#EF4444', border: '1px solid #FCA5A5' }} containerStyle={{ flex: 1 }} onClick={() => setShowConfirmModal('rejected')} />
                <components.Button title={isLoading ? t.leaveDetails.processing : t.leaveDetails.modal.approveBtn} containerStyle={{ flex: 1 }} onClick={() => setShowConfirmModal('approved')} />
              </div>
            )}
            
            {leave.user_id == authSlice.userId && (
              <components.Button 
                title={isLoading ? t.leaveDetails.processing : t.leaveDetails.modal.cancelTitle} 
                style={{ backgroundColor: '#F3F4F6', color: '#4B5563', border: '1px solid #E5E7EB' }} 
                containerStyle={{ width: '100%' }} 
                onClick={() => setShowConfirmModal('delete')} 
              />
            )}
          </div>
        </div>
      )}
      {renderConfirmModal()}
      {renderAlert()}
    </div>
  );
};